/**
 * Attachment encryption.
 *
 * Files are encrypted with a fresh key on the sender's device and uploaded as
 * opaque blobs. The key travels inside the message ciphertext, so the server
 * holds bytes it can never open — and a blob leak on its own reveals nothing.
 */
import {
  aeadDecrypt,
  aeadEncrypt,
  fromB64,
  randomBytes,
  sha256,
  timingSafeEqual,
  toB64,
  KEY_LEN,
  NONCE_LEN,
} from './primitives.ts';
import type { AttachmentRef } from '../types.ts';

export class MediaIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MediaIntegrityError';
  }
}

export interface EncryptedAttachment {
  /** Opaque bytes to upload. */
  blob: Uint8Array;
  /** Everything but `mediaId`, which only exists after the upload succeeds. */
  ref: Omit<AttachmentRef, 'mediaId'>;
}

export interface AttachmentMeta {
  mime: string;
  name: string;
  width?: number;
  height?: number;
  durationMs?: number;
  /** True for a recorded voice note rather than an attached audio file. */
  voice?: boolean;
  /** Precomputed amplitude peaks, so the reader can draw bars before download. */
  waveform?: Uint8Array;
  /** Small preview bytes, encrypted under the same key as a separate payload. */
  thumbnail?: Uint8Array;
}

export function encryptAttachment(data: Uint8Array, meta: AttachmentMeta): EncryptedAttachment {
  const key = randomBytes(KEY_LEN);
  const nonce = randomBytes(NONCE_LEN);
  const blob = aeadEncrypt(key, nonce, data);

  // Digest the ciphertext, not the plaintext: it lets the recipient reject a
  // tampered blob before spending time and memory decrypting it, and it does
  // not leak a hash of the plaintext to the server.
  const digest = sha256(blob);

  let thumbnail: string | undefined;
  if (meta.thumbnail) {
    // Distinct nonce, same key — reusing the nonce would be catastrophic for a
    // stream cipher, so this must never be shared with the main body.
    const thumbNonce = randomBytes(NONCE_LEN);
    const ct = aeadEncrypt(key, thumbNonce, meta.thumbnail);
    thumbnail = `${toB64(thumbNonce)}.${toB64(ct)}`;
  }

  return {
    blob,
    ref: {
      key: toB64(key),
      nonce: toB64(nonce),
      sha256: toB64(digest),
      mime: meta.mime,
      name: meta.name,
      size: data.length,
      width: meta.width,
      height: meta.height,
      durationMs: meta.durationMs,
      voice: meta.voice,
      waveform: meta.waveform ? toB64(meta.waveform) : undefined,
      thumbnail,
    },
  };
}

export function decryptAttachment(blob: Uint8Array, ref: AttachmentRef): Uint8Array {
  if (!timingSafeEqual(sha256(blob), fromB64(ref.sha256))) {
    throw new MediaIntegrityError(`attachment ${ref.mediaId} failed its digest check`);
  }
  return aeadDecrypt(fromB64(ref.key), fromB64(ref.nonce), blob);
}

export function decryptThumbnail(ref: AttachmentRef): Uint8Array | undefined {
  if (!ref.thumbnail) return undefined;
  const [nonce, ct] = ref.thumbnail.split('.');
  return aeadDecrypt(fromB64(ref.key), fromB64(nonce), fromB64(ct));
}
